import React, { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  CheckCircle, ArrowLeft, Euro, Calendar, FileText,
  CreditCard, Banknote, Smartphone, Building,
  Receipt, AlertCircle, Clock, Package, User,
  Calculator, Save, CheckCircle2, Wrench, ShoppingCart,
  X, Search
} from 'lucide-react'

import Button from '../components/ui/Button'
import Input from '../components/ui/Input'
import Select from '../components/ui/Select'
import TextArea from '../components/ui/Textarea'
import Badge from '../components/ui/Badge'
import Modal from '../components/Modal'
import { useToast } from '../components/Toast'

import { OrdemServico, PagamentoVenda, Produto } from '../types'
import ordensServicoService from '../services/ordensServico'
import { formatValor } from '../services/ordensServico'
import orcamentosService, { Orcamento } from '../services/orcamentos'
import produtosService from '../services/produtos'

interface PagamentoFormData {
  formaPagamento: 'DINHEIRO' | 'MBWAY' | 'MULTIBANCO' | 'TRANSFERENCIA'
  valor: number
  referencia?: string
  observacoes?: string
}

interface ItemBaixaEstoque {
  produtoId: number | null
  quantidade: number
  descricao: string
  tipo: 'PECA' | 'SERVICO'
  valorUnitario: number
  selected: boolean
}

interface FecharOSFormData {
  valorFinal: number
  observacoesFechamento?: string
  entregueAoCliente: boolean
  pagamentos: PagamentoFormData[]
  itensBaixaEstoque: Array<{
    produtoId: number
    quantidade: number
    descricao: string
  }>
}

const FecharOSPage: React.FC = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { success, error } = useToast()
  const queryClient = useQueryClient()

  const [formData, setFormData] = useState<FecharOSFormData>({
    valorFinal: 0,
    observacoesFechamento: '',
    entregueAoCliente: false,
    pagamentos: [],
    itensBaixaEstoque: []
  })

  // Estados para modais em sequência
  const [modalStep, setModalStep] = useState<'items' | 'payment' | null>(null)
  const [itensBaixa, setItensBaixa] = useState<ItemBaixaEstoque[]>([])
  const [orcamentoAprovado, setOrcamentoAprovado] = useState<Orcamento | null>(null)

  // Estado para produtos extras
  const [produtosExtras, setProdutosExtras] = useState<ItemBaixaEstoque[]>([])
  const [novoProduto, setNovoProduto] = useState({
    produtoId: null as number | null,
    descricao: '',
    quantidade: 1,
    valorUnitario: 0
  })
  const [buscaProduto, setBuscaProduto] = useState('')
  const [produtosSugestoes, setProdutosSugestoes] = useState<any[]>([])
  const [mostrarSugestoes, setMostrarSugestoes] = useState(false)

  const [currentPayment, setCurrentPayment] = useState<PagamentoFormData>({
    formaPagamento: 'DINHEIRO',
    valor: 0,
    referencia: '',
    observacoes: ''
  })

  // Calcula valor pendente com validação para evitar NaN
  const totalPagamentos = formData.pagamentos.reduce((acc, pag) => acc + (pag.valor || 0), 0)
  const valorFinalValido = Number(formData.valorFinal) || 0
  const valorPendente = valorFinalValido - totalPagamentos
  const isPagamentoCompleto = valorPendente <= 0

  // Atualizar valor automaticamente quando forma de pagamento mudar
  useEffect(() => {
    // Para MBWay, Multibanco e Transferência, usar valor exato pendente
    if (['MBWAY', 'MULTIBANCO', 'TRANSFERENCIA'].includes(currentPayment.formaPagamento)) {
      setCurrentPayment(prev => ({
        ...prev,
        valor: Math.max(0, valorPendente)
      }))
    } else {
      // Para dinheiro, limpar o valor (usuário digita)
      setCurrentPayment(prev => ({
        ...prev,
        valor: 0
      }))
    }
  }, [currentPayment.formaPagamento, valorPendente])

  // Query para buscar a OS
  const { data: ordemServico, isLoading } = useQuery({
    queryKey: ['ordem-servico', id],
    queryFn: () => ordensServicoService.getById(parseInt(id!)),
    enabled: !!id
  })

  // Inicializar form com valores da OS (usando valor com IVA)
  useEffect(() => {
    if (ordemServico) {
      // Usar valorTotalComIva se disponível, senão calcular
      const valorBase = Number(ordemServico.valorOrcamento) || 0
      const valorComIva = Number(ordemServico.valorTotalComIva) || (valorBase * 1.23) // 23% IVA

      // Garantir que é um número válido
      const valorFinalCalculado = isNaN(valorComIva) ? 0 : valorComIva

      setFormData(prev => ({
        ...prev,
        valorFinal: valorFinalCalculado
      }))
    }
  }, [ordemServico])

  // Buscar orçamento aprovado quando a OS for carregada
  useEffect(() => {
    const fetchOrcamentoAprovado = async () => {
      if (ordemServico?.id && ordemServico.orcamentoAprovado) {
        try {
          console.log('🔍 Buscando orçamento para OS:', ordemServico.id)

          // Buscar TODOS os orçamentos da OS (sem filtro de status)
          const response = await orcamentosService.list({
            ordemServicoId: ordemServico.id,
            limit: 10 // Buscar os últimos 10
          })

          console.log('📦 Resposta da API de orçamentos:', response)

          if (response.orcamentos && response.orcamentos.length > 0) {
            // Pegar o orçamento mais recente que NÃO esteja rejeitado/expirado
            const orcamento = response.orcamentos.find(
              orc => !['REJEITADO', 'EXPIRADO'].includes(orc.status)
            ) || response.orcamentos[0]

            console.log('✅ Orçamento encontrado:', orcamento)
            console.log('📋 Itens do orçamento:', orcamento.itens)

            setOrcamentoAprovado(orcamento)

            // Converter itens do orçamento para itens de baixa
            const itensConvertidos: ItemBaixaEstoque[] = orcamento.itens.map(item => ({
              produtoId: null, // Será preenchido se for peça
              quantidade: item.quantidade,
              descricao: item.descricao,
              tipo: item.tipo,
              valorUnitario: item.valorUnitario,
              selected: item.tipo === 'PECA' // Peças selecionadas por padrão
            }))

            console.log('🔄 Itens convertidos:', itensConvertidos)
            setItensBaixa(itensConvertidos)

            // Calcular valor final baseado nos itens do orçamento (não no valorOrcamento estimado)
            const totalItens = orcamento.itens.reduce((acc, item) => {
              return acc + (item.quantidade * item.valorUnitario)
            }, 0)
            const totalComIva = totalItens * 1.23 // 23% IVA

            console.log("💰 Total itens:", totalItens, "Com IVA:", totalComIva)

            setFormData(prev => ({
              ...prev,
              valorFinal: totalComIva
            }))
          } else {
            console.warn('⚠️ Nenhum orçamento encontrado para esta OS')
            console.warn('Resposta completa:', response)
          }
        } catch (err) {
          console.error('❌ Erro ao buscar orçamento:', err)
        }
      }
    }

    fetchOrcamentoAprovado()
  }, [ordemServico])

  // Buscar produtos quando o usuário digitar
  useEffect(() => {
    const buscarProdutos = async () => {
      if (buscaProduto.length < 2) {
        setProdutosSugestoes([])
        return
      }

      try {
        const response = await produtosService.list({ search: buscaProduto, limit: 10 })
        setProdutosSugestoes(response.produtos || [])
      } catch (err) {
        console.error('Erro ao buscar produtos:', err)
        setProdutosSugestoes([])
      }
    }

    const debounceTimer = setTimeout(buscarProdutos, 300)
    return () => clearTimeout(debounceTimer)
  }, [buscaProduto])

  // Mutation para fechar OS
  const fecharOSMutation = useMutation({
    mutationFn: async (data: FecharOSFormData) => {
      return ordensServicoService.fecharOS(parseInt(id!), data)
    },
    onSuccess: (response, variables) => {
      success('Ordem de serviço fechada com sucesso!')
      queryClient.invalidateQueries(['ordem-servico'])
      queryClient.invalidateQueries(['ordens-servico'])

      // Redirecionar para a página de sucesso
      navigate(`/ordens-servico/${id}/fechamento-success`)
    },
    onError: (err: any) => {
      error('Erro ao fechar OS', err.message)
    }
  })

  // Opções de formas de pagamento
  const formasPagamento = [
    { value: 'DINHEIRO', label: 'Dinheiro', icon: <Banknote className="w-4 h-4" /> },
    { value: 'MBWAY', label: 'MBWay', icon: <Smartphone className="w-4 h-4" /> },
    { value: 'MULTIBANCO', label: 'Multibanco', icon: <Building className="w-4 h-4" /> },
    { value: 'TRANSFERENCIA', label: 'Transferência Bancária', icon: <Building className="w-4 h-4" /> }
  ]

  // Calcular troco
  const troco = totalPagamentos > formData.valorFinal ? totalPagamentos - formData.valorFinal : 0

  const handleAddPayment = () => {
    if (currentPayment.valor <= 0) {
      error('Valor do pagamento deve ser maior que zero')
      return
    }

    setFormData(prev => ({
      ...prev,
      pagamentos: [...prev.pagamentos, { ...currentPayment }]
    }))

    // Resetar formulário de pagamento
    setCurrentPayment({
      formaPagamento: 'DINHEIRO',
      valor: Math.max(0, valorPendente - currentPayment.valor), // Preencher com o restante pendente
      referencia: '',
      observacoes: ''
    })
  }

  const handleRemovePayment = (index: number) => {
    setFormData(prev => ({
      ...prev,
      pagamentos: prev.pagamentos.filter((_, i) => i !== index)
    }))
  }

  // Funções para produtos extras
  const handleSelecionarProduto = (produto: Produto) => {
    setNovoProduto({
      produtoId: produto.id,
      descricao: produto.nome,
      quantidade: 1,
      valorUnitario: Number(produto.precoVenda) || 0
    })
    setBuscaProduto(produto.nome)
    setMostrarSugestoes(false)
    setProdutosSugestoes([])
  }

  const handleAdicionarProdutoExtra = () => {
    if (!novoProduto.descricao || novoProduto.valorUnitario <= 0 || novoProduto.quantidade <= 0) {
      error('Preencha todos os campos corretamente')
      return
    }

    const novoItem: ItemBaixaEstoque = {
      produtoId: novoProduto.produtoId,
      quantidade: novoProduto.quantidade,
      descricao: novoProduto.descricao,
      tipo: 'PECA',
      valorUnitario: novoProduto.valorUnitario,
      selected: false
    }

    setProdutosExtras(prev => [...prev, novoItem])

    // Atualizar valor final com validação
    const valorExtra = (Number(novoProduto.quantidade) || 0) * (Number(novoProduto.valorUnitario) || 0)
    setFormData(prev => ({
      ...prev,
      valorFinal: (Number(prev.valorFinal) || 0) + valorExtra
    }))

    // Limpar formulário
    setNovoProduto({
      produtoId: null,
      descricao: '',
      quantidade: 1,
      valorUnitario: 0
    })
    setBuscaProduto('')
    setProdutosSugestoes([])

    success('Produto extra adicionado!')
  }

  const handleRemoverProdutoExtra = (index: number) => {
    const produtoRemovido = produtosExtras[index]
    const valorRemover = (Number(produtoRemovido.quantidade) || 0) * (Number(produtoRemovido.valorUnitario) || 0)

    setProdutosExtras(prev => prev.filter((_, i) => i !== index))

    // Atualizar valor final com validação
    setFormData(prev => ({
      ...prev,
      valorFinal: (Number(prev.valorFinal) || 0) - valorRemover
    }))
  }

  // Iniciar fluxo de fechamento com modais em sequência
  const handleInitiarFechamento = () => {
    // Validar que o valor é um número válido (pode ser zero para garantias/diagnósticos gratuitos)
    const valorValido = Number(formData.valorFinal)

    if (isNaN(valorValido) || valorValido < 0) {
      error('Valor final deve ser um número válido (maior ou igual a zero)')
      return
    }

    // Abrir o primeiro modal (confirmação de peças/serviços)
    setModalStep('items')
  }

  // Confirmar itens para baixa de estoque
  const handleConfirmarItens = () => {
    // Enviar TODOS os itens (serviços + peças) para o backend
    // O backend irá criar produtos de serviço quando necessário
    const todosItens = itensBaixa.map(item => ({
      produtoId: item.produtoId, // null para serviços, será preenchido pelo backend
      quantidade: item.quantidade,
      descricao: item.descricao,
      tipo: item.tipo,
      valorUnitario: item.valorUnitario,
      selected: item.selected // Manter informação de seleção para baixa de estoque
    }))

    // Adicionar produtos extras
    const itensExtras = produtosExtras.map(item => ({
      produtoId: item.produtoId,
      quantidade: item.quantidade,
      descricao: item.descricao,
      tipo: item.tipo,
      valorUnitario: item.valorUnitario,
      selected: true // Produtos extras sempre selecionados para baixa
    }))

    // Combinar todos os itens
    const todosItensCombinados = [...todosItens, ...itensExtras]

    // Atualizar formData com TODOS os itens
    setFormData(prev => ({
      ...prev,
      itensBaixaEstoque: todosItensCombinados as any
    }))

    console.log('📦 Itens sendo enviados:', todosItensCombinados)

    // Avançar para modal de pagamento
    setModalStep('payment')
  }

  // Confirmar pagamento e fechar OS
  const handleConfirmarPagamento = () => {
    if (!isPagamentoCompleto && formData.entregueAoCliente) {
      error('Não é possível entregar ao cliente sem pagamento completo')
      return
    }

    // Fechar OS diretamente
    const dataToSubmit = {
      ...formData,
      pagamentos: formData.pagamentos.length > 0 ? formData.pagamentos : [],
      itensBaixaEstoque: formData.itensBaixaEstoque || []
    }

    console.log('Fechando OS:', dataToSubmit)

    fecharOSMutation.mutate(dataToSubmit)
    setModalStep(null)
  }

  const getStatusColor = (estado: string) => {
    const name = estado.toLowerCase()
    if (name.includes('conclu') || name.includes('entregue')) return 'green'
    if (name.includes('cancel') || name.includes('recus')) return 'red'
    if (name.includes('anda') || name.includes('repar')) return 'blue'
    if (name.includes('espera') || name.includes('pend')) return 'yellow'
    return 'gray'
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Carregando ordem de serviço...</p>
        </div>
      </div>
    )
  }

  if (!ordemServico) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Ordem de Serviço Não Encontrada</h2>
          <p className="text-gray-600 mb-4">A ordem de serviço solicitada não foi encontrada.</p>
          <Button onClick={() => navigate('/ordens-servico')}>
            Voltar às Ordens de Serviço
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-4 mb-4">
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate(-1)}
              leftIcon={<ArrowLeft className="w-4 h-4" />}
            >
              Voltar
            </Button>
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <CheckCircle className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Fechar Ordem de Serviço</h1>
                <p className="text-gray-600">Finalizar e receber pagamento da OS #{ordemServico.numero}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Informações da OS */}
          <div className="lg:col-span-2 space-y-6">
            {/* Resumo da OS */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <FileText className="w-5 h-5" />
                Resumo da Ordem de Serviço
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-500">Número OS</label>
                    <p className="text-lg font-semibold text-gray-900">#{ordemServico.numero}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-500">Cliente</label>
                    <p className="text-gray-900">{ordemServico.cliente.nome}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-500">Estado Atual</label>
                    <Badge
                      variant={getStatusColor(ordemServico.estado.nome) as any}
                      text={ordemServico.estado.nome}
                    />
                  </div>
                </div>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-500">Equipamento</label>
                    <p className="text-gray-900">
                      {ordemServico.equipamento.modelo.marca.nome} {ordemServico.equipamento.modelo.nome}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-500">Data Entrada</label>
                    <p className="text-gray-900">
                      {new Date(ordemServico.dataEntrada).toLocaleDateString('pt-PT')}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-500">Valor Total c/ IVA</label>
                    <p className="text-lg font-semibold text-gray-900">
                      {formatValor(ordemServico.valorTotalComIva || (ordemServico.valorOrcamento * 1.23))}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Valores Finais */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Calculator className="w-5 h-5" />
                Valores Finais
              </h2>

              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <label className="block text-xs font-medium text-gray-500">Valor Base</label>
                    <p className="text-sm font-semibold text-gray-900">
                      {formatValor((formData.valorFinal / 1.23))}
                    </p>
                  </div>
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <label className="block text-xs font-medium text-gray-500">IVA (23%)</label>
                    <p className="text-sm font-semibold text-gray-900">
                      {formatValor(formData.valorFinal - (formData.valorFinal / 1.23))}
                    </p>
                  </div>
                  <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                    <label className="block text-xs font-medium text-blue-600">Total c/ IVA</label>
                    <p className="text-lg font-bold text-blue-900">
                      {formatValor(formData.valorFinal)}
                    </p>
                  </div>
                </div>

                <Input
                  label="Valor Final Total (com IVA)"
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.valorFinal}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    valorFinal: parseFloat(e.target.value) || 0
                  }))}
                  placeholder="0.00"
                  hint="Este valor já inclui IVA a 23%"
                />

                <TextArea
                  label="Observações do Fechamento"
                  placeholder="Observações adicionais sobre o fechamento da OS..."
                  value={formData.observacoesFechamento}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    observacoesFechamento: e.target.value
                  }))}
                  rows={3}
                />
              </div>
            </div>

            {/* Informação sobre o processo */}
            <div className="bg-blue-50 rounded-lg border border-blue-200 p-6">
              <div className="flex items-start gap-3">
                <CheckCircle className="h-6 w-6 text-blue-600 mt-0.5" />
                <div>
                  <h3 className="text-lg font-semibold text-blue-900 mb-2">Processo de Fechamento</h3>
                  <p className="text-sm text-blue-700">
                    Ao clicar em "Fechar Ordem de Serviço", você será guiado através dos seguintes passos:
                  </p>
                  <ol className="text-sm text-blue-700 mt-2 ml-4 list-decimal space-y-1">
                    <li>Confirmação de peças e serviços para baixa de stock</li>
                    <li>Registro de formas de pagamento</li>
                    <li>Escolha do envio de recibo (impressão, email, WhatsApp ou nenhum)</li>
                  </ol>
                </div>
              </div>
            </div>
          </div>

          {/* Sidebar - Resumo */}
          <div className="space-y-6">
            {/* Resumo Financeiro */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Resumo Financeiro</h3>

              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-gray-600 text-sm">Valor Base:</span>
                  <span className="text-sm text-gray-900">
                    {formatValor(formData.valorFinal / 1.23)}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600 text-sm">IVA (23%):</span>
                  <span className="text-sm text-gray-900">
                    {formatValor(formData.valorFinal - (formData.valorFinal / 1.23))}
                  </span>
                </div>
                <div className="flex justify-between items-center border-t pt-2">
                  <span className="text-gray-900 font-medium">Total c/ IVA:</span>
                  <span className="font-bold text-lg text-gray-900">
                    {formatValor(formData.valorFinal)}
                  </span>
                </div>
              </div>
            </div>

            {/* Ações */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Ações</h3>

              <div className="space-y-3">
                <Button
                  fullWidth
                  onClick={handleInitiarFechamento}
                  isLoading={fecharOSMutation.isPending}
                  leftIcon={<Save className="w-4 h-4" />}
                >
                  Fechar Ordem de Serviço
                </Button>

                <Button
                  variant="outline"
                  fullWidth
                  onClick={() => navigate(-1)}
                  disabled={fecharOSMutation.isPending}
                >
                  Cancelar
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modal 1: Confirmação de Peças/Serviços */}
      <Modal
        isOpen={modalStep === 'items'}
        onClose={() => setModalStep(null)}
        title="Confirmar Peças e Serviços"
        size="lg"
      >
        <div className="space-y-4">
          <div className="bg-blue-50 rounded-lg p-4 mb-4">
            <div className="flex items-start gap-3">
              <ShoppingCart className="h-5 w-5 text-blue-600 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-blue-900">
                  Itens do Orçamento Aprovado
                </p>
                <p className="text-sm text-blue-700 mt-1">
                  Selecione as peças que devem ter baixa no stock. Serviços não afetam o stock.
                </p>
              </div>
            </div>
          </div>

          {itensBaixa.length === 0 ? (
            <div className="text-center py-8 border-2 border-dashed border-gray-200 rounded-lg">
              <Package className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500">Nenhum orçamento aprovado encontrado</p>
              <p className="text-sm text-gray-400">Você pode prosseguir sem dar baixa em itens</p>
            </div>
          ) : (
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {itensBaixa.map((item, index) => (
                <div
                  key={index}
                  className={`p-3 rounded-lg border ${
                    item.tipo === 'PECA' ? 'bg-white' : 'bg-gray-50'
                  } ${item.tipo === 'PECA' ? 'hover:border-blue-300' : ''}`}
                >
                  <div className="flex items-start gap-3">
                    <input
                      type="checkbox"
                      checked={item.selected}
                      onChange={(e) => {
                        const novosItens = [...itensBaixa]
                        novosItens[index].selected = e.target.checked
                        setItensBaixa(novosItens)
                      }}
                      disabled={item.tipo === 'SERVICO'}
                      className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded disabled:opacity-50"
                    />
                    <div className="flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        {item.tipo === 'PECA' ? (
                          <Package className="h-4 w-4 text-blue-600 flex-shrink-0" />
                        ) : (
                          <Wrench className="h-4 w-4 text-gray-400 flex-shrink-0" />
                        )}
                        <span className="font-medium text-gray-900">{item.descricao}</span>
                        <Badge
                          variant={item.tipo === 'PECA' ? 'blue' : 'gray'}
                          text={item.tipo === 'PECA' ? 'Peça' : 'Serviço'}
                        />
                      </div>
                      <div className="text-sm text-gray-500 mt-1">
                        Quantidade: {item.quantidade} | Valor Unit.: {formatValor(item.valorUnitario)} | Total: {formatValor(item.quantidade * item.valorUnitario)}
                      </div>
                      {item.tipo === 'SERVICO' && (
                        <p className="text-xs text-gray-400 mt-1">Serviços não afetam o stock</p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Seção de Produtos Extras */}
          <div className="border-t pt-4 mt-4">
            <div className="bg-green-50 rounded-lg p-4 mb-3">
              <div className="flex items-start gap-3">
                <ShoppingCart className="h-5 w-5 text-green-600 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-green-900">
                    Adicionar Produtos Extras
                  </p>
                  <p className="text-sm text-green-700 mt-1">
                    Cliente quer comprar produtos adicionais? Adicione aqui.
                  </p>
                </div>
              </div>
            </div>

            {/* Produtos extras adicionados */}
            {produtosExtras.length > 0 && (
              <div className="space-y-2 mb-3">
                <label className="text-sm font-medium text-gray-700">Produtos Adicionados:</label>
                {produtosExtras.map((produto, index) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-green-50 rounded-lg border border-green-200">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <Package className="h-4 w-4 text-green-600" />
                        <span className="font-medium text-gray-900">{produto.descricao}</span>
                      </div>
                      <div className="text-sm text-gray-500 mt-1">
                        Qtd: {produto.quantidade} | Valor Unit.: {formatValor(produto.valorUnitario)} | Total: {formatValor(produto.quantidade * produto.valorUnitario)}
                      </div>
                    </div>
                    <button
                      onClick={() => handleRemoverProdutoExtra(index)}
                      className="text-red-600 hover:text-red-800 text-sm ml-3"
                    >
                      <X className="h-5 w-5" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Formulário para adicionar produto extra */}
            <div className="grid grid-cols-12 gap-2">
              <div className="col-span-6 relative">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Buscar Produto
                </label>
                <div className="relative">
                  <input
                    type="text"
                    value={buscaProduto}
                    onChange={(e) => {
                      setBuscaProduto(e.target.value)
                      setMostrarSugestoes(true)
                    }}
                    onFocus={() => setMostrarSugestoes(true)}
                    placeholder="Digite para buscar produtos..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                  <Search className="absolute right-3 top-2.5 h-5 w-5 text-gray-400" />
                </div>

                {/* Lista de sugestões */}
                {mostrarSugestoes && produtosSugestoes.length > 0 && (
                  <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto">
                    {produtosSugestoes.map((produto) => (
                      <button
                        key={produto.id}
                        type="button"
                        onClick={() => handleSelecionarProduto(produto)}
                        className="w-full text-left px-4 py-2 hover:bg-blue-50 border-b border-gray-100 last:border-b-0"
                      >
                        <div className="flex justify-between items-center">
                          <div>
                            <p className="font-medium text-gray-900">{produto.nome}</p>
                            <p className="text-xs text-gray-500">SKU: {produto.sku || produto.codigoInterno} | Stock: {produto.stockAtual}</p>
                          </div>
                          <span className="text-sm font-semibold text-blue-600">{formatValor(produto.precoVenda || 0)}</span>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <div className="col-span-2">
                <Input
                  label="Qtd"
                  type="number"
                  min="1"
                  value={novoProduto.quantidade}
                  onChange={(e) => setNovoProduto(prev => ({ ...prev, quantidade: parseInt(e.target.value) || 1 }))}
                />
              </div>
              <div className="col-span-3">
                <Input
                  label="Valor (€)"
                  type="number"
                  step="0.01"
                  min="0"
                  value={novoProduto.valorUnitario || ''}
                  onChange={(e) => setNovoProduto(prev => ({ ...prev, valorUnitario: parseFloat(e.target.value) || 0 }))}
                  placeholder="0.00"
                />
              </div>
              <div className="col-span-1 flex items-end">
                <Button
                  size="sm"
                  onClick={handleAdicionarProdutoExtra}
                  className="w-full"
                  title="Adicionar"
                >
                  +
                </Button>
              </div>
            </div>
          </div>

          <div className="flex justify-between items-center pt-4 border-t">
            <Button
              variant="outline"
              onClick={() => setModalStep(null)}
            >
              Cancelar
            </Button>
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => setModalStep('payment')}
              >
                Pular Baixa de Stock
              </Button>
              <Button
                onClick={handleConfirmarItens}
                leftIcon={<CheckCircle className="w-4 h-4" />}
              >
                Confirmar e Continuar
              </Button>
            </div>
          </div>
        </div>
      </Modal>

      {/* Modal 2: Forma de Pagamento */}
      <Modal
        isOpen={modalStep === 'payment'}
        onClose={() => setModalStep(null)}
        title="Registrar Pagamento"
        size="lg"
      >
        <div className="space-y-4">
          <div className="bg-blue-50 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <Euro className="h-5 w-5 text-blue-600 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-medium text-blue-900">Resumo Financeiro</p>
                <div className="mt-2 space-y-1">
                  <div className="flex justify-between text-sm">
                    <span className="text-blue-700">Valor Total:</span>
                    <span className="font-semibold text-blue-900">{formatValor(formData.valorFinal)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-blue-700">Total Pago:</span>
                    <span className="font-semibold text-blue-900">{formatValor(totalPagamentos)}</span>
                  </div>
                  <div className="flex justify-between text-sm border-t border-blue-200 pt-1">
                    <span className="text-blue-700 font-medium">Pendente:</span>
                    <span className={`font-bold ${valorPendente > 0 ? 'text-red-600' : 'text-green-600'}`}>
                      {formatValor(Math.max(0, valorPendente))}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Lista de Pagamentos */}
          {formData.pagamentos.length > 0 && (
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">Pagamentos Registrados:</label>
              {formData.pagamentos.map((pag, idx) => (
                <div key={idx} className="flex justify-between items-center p-3 bg-gray-50 rounded border">
                  <div className="flex items-center gap-2">
                    {formasPagamento.find(f => f.value === pag.formaPagamento)?.icon}
                    <span className="text-sm font-medium">{formasPagamento.find(f => f.value === pag.formaPagamento)?.label}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-sm font-semibold">{formatValor(pag.valor)}</span>
                    <button
                      onClick={() => handleRemovePayment(idx)}
                      className="text-red-600 hover:text-red-800 text-xs"
                    >
                      Remover
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Adicionar Pagamento */}
          {valorPendente > 0 && (
            <div className="border-t pt-4">
              <h4 className="text-sm font-medium text-gray-700 mb-3">Adicionar Pagamento</h4>
              <div className="space-y-3">
                <Select
                  label="Forma de Pagamento"
                  value={currentPayment.formaPagamento}
                  onChange={(e) => setCurrentPayment(prev => ({
                    ...prev,
                    formaPagamento: e.target.value as any
                  }))}
                  options={formasPagamento.map(f => ({
                    value: f.value,
                    label: f.label
                  }))}
                />

                {currentPayment.formaPagamento === 'DINHEIRO' ? (
                  <Input
                    label="Valor Recebido (€)"
                    type="number"
                    step="0.01"
                    min="0"
                    value={currentPayment.valor || ''}
                    onChange={(e) => setCurrentPayment(prev => ({
                      ...prev,
                      valor: parseFloat(e.target.value) || 0
                    }))}
                    onFocus={(e) => e.target.select()}
                    placeholder="Digite o valor recebido"
                    hint={`Valor pendente: ${formatValor(valorPendente)}`}
                  />
                ) : (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                    <p className="text-sm text-green-800">
                      <strong>Valor do pagamento:</strong> {formatValor(currentPayment.valor)}
                    </p>
                    <p className="text-xs text-green-600 mt-1">
                      Pagamento eletrônico com valor exato pendente
                    </p>
                  </div>
                )}

                <Button
                  size="sm"
                  onClick={handleAddPayment}
                  disabled={currentPayment.valor <= 0}
                  leftIcon={<CreditCard className="w-4 h-4" />}
                >
                  Adicionar Pagamento
                </Button>
              </div>
            </div>
          )}

          {/* Entrega ao Cliente */}
          <div className="border-t pt-4">
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.entregueAoCliente}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  entregueAoCliente: e.target.checked
                }))}
                className="mt-1 rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50 cursor-pointer"
              />
              <div className="flex-1">
                <span className="font-medium text-gray-900">Entregue ao Cliente</span>
                <p className="text-sm text-gray-500 mt-1">
                  Marque se o equipamento for entregue agora
                </p>
                {!isPagamentoCompleto && formData.entregueAoCliente && (
                  <p className="text-sm text-amber-600 mt-1 flex items-center gap-1">
                    <AlertCircle className="h-4 w-4" />
                    Atenção: O pagamento ainda não está completo
                  </p>
                )}
              </div>
            </label>
          </div>

          {/* Ações */}
          <div className="flex justify-between items-center pt-4 border-t">
            <Button
              variant="outline"
              onClick={() => setModalStep('items')}
              leftIcon={<ArrowLeft className="w-4 h-4" />}
            >
              Voltar
            </Button>
            <div className="flex gap-2">
              {valorPendente > 0 && (
                <Button
                  variant="outline"
                  onClick={handleConfirmarPagamento}
                >
                  Fechar Sem Receber
                </Button>
              )}
              <Button
                onClick={handleConfirmarPagamento}
                disabled={valorPendente > 0 && formData.pagamentos.length === 0}
                leftIcon={<CheckCircle className="w-4 h-4" />}
              >
                Confirmar e Continuar
              </Button>
            </div>
          </div>
        </div>
      </Modal>

    </div>
  )
}

export default FecharOSPage